// التنقّل بأسهم الريموت على التلفزيون.
//
// WebView أندرويد لا ينقل التركيز بين الأزرار بالأسهم: يمرّر الصفحة فقط.
// فيُختار هنا العنصر الأقرب في الاتجاه المطلوب. والاتجاه بصريّ لا منطقيّ:
// الريموت يشير إلى الشاشة، فـ«يمين» يمينُها في العربية والإنجليزية سواء.

const FOCUSABLE =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const KEYS = { ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right' };

const centerX = (r) => (r.left + r.right) / 2;
const centerY = (r) => (r.top + r.bottom) / 2;

/**
 * الأقرب في الاتجاه. البعد على محور الاتجاه أولاً، والانحراف عنه مضاعفاً —
 * فالمحاذي يسبق القطريّ القريب، كما تتوقّع العين.
 */
export function pickNext(from, candidates, direction) {
  let best = null;
  let bestScore = Infinity;

  for (const { el, rect } of candidates) {
    if (rect === from) continue;
    let ahead;
    let gap;
    let drift;

    if (direction === 'right' || direction === 'left') {
      ahead = direction === 'right' ? centerX(rect) > centerX(from) + 1 : centerX(rect) < centerX(from) - 1;
      gap = direction === 'right' ? Math.max(0, rect.left - from.right) : Math.max(0, from.left - rect.right);
      const overlaps = rect.top < from.bottom && rect.bottom > from.top;
      drift = overlaps ? 0 : Math.abs(centerY(rect) - centerY(from));
    } else {
      ahead = direction === 'down' ? centerY(rect) > centerY(from) + 1 : centerY(rect) < centerY(from) - 1;
      gap = direction === 'down' ? Math.max(0, rect.top - from.bottom) : Math.max(0, from.top - rect.bottom);
      const overlaps = rect.left < from.right && rect.right > from.left;
      drift = overlaps ? 0 : Math.abs(centerX(rect) - centerX(from));
    }

    if (!ahead) continue;
    const score = gap + drift * 2;
    if (score < bestScore) {
      bestScore = score;
      best = el;
    }
  }
  return best;
}

/** النافذة المفتوحة الأخيرة تحبس التركيز، وإلا فالصفحة كلها. */
function scope(root) {
  const dialogs = root.querySelectorAll('[role="dialog"][aria-modal="true"]');
  return dialogs.length ? dialogs[dialogs.length - 1] : root;
}

// حقول الكتابة تفتح لوحة مفاتيح التلفزيون فتأسر الأسهم ويعلق المستخدم
// (مقيسٌ على محاكٍ). والكتابة خارج نسخة التلفزيون أصلاً؛ القوائم المنسدلة لا.
const TEXT_ENTRY =
  'textarea, input:not([type]), input[type="text"], input[type="search"], input[type="email"], input[type="tel"], input[type="password"], input[type="number"], input[type="url"]';

function candidatesIn(container) {
  return [...container.querySelectorAll(FOCUSABLE)]
    .filter((el) => !el.matches(TEXT_ENTRY) && el.getClientRects().length > 0)
    .map((el) => ({ el, rect: el.getBoundingClientRect() }));
}

export function installSpatialNav(root = document) {
  const onKey = (e) => {
    const direction = KEYS[e.key];
    if (!direction) return;
    // داخل حقل نصّ: اليمين واليسار يحرّكان المؤشّر لا التركيز
    const active = root.activeElement;
    if ((direction === 'left' || direction === 'right') && active?.matches?.('input, textarea')) return;

    const list = candidatesIn(scope(root));
    if (!list.length) return;
    e.preventDefault();

    const current = list.find((c) => c.el === active);
    const next = current ? pickNext(current.rect, list, direction) : list[0].el;
    if (!next) return;
    next.focus({ preventScroll: true });
    next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  root.addEventListener('keydown', onKey);
  return () => root.removeEventListener('keydown', onKey);
}
